import { Injectable } from '@angular/core';
import {
	SignatureRemovalRequest,
	SignatureRemovalRequestInterface,
	SignatureRemovalRequestPayload,
} from './signature-removal-request';
import { SignatureRemovalRequestSearchParamsInterface } from './signature-removal-request-search-params';
import { BaseModelService } from '@app/abstracts';
import { Helpers } from '@app/shared/helpers';

@Injectable()
export class SignatureRemovalRequestService extends BaseModelService<
	SignatureRemovalRequest,
	SignatureRemovalRequestInterface,
	SignatureRemovalRequestPayload,
	SignatureRemovalRequestSearchParamsInterface
> {
	/** @inheritDoc */
	protected path(): string {
		return 'signature-removal-request';
	}

	/** @inheritDoc */
	protected new(
		object: SignatureRemovalRequestInterface
	): SignatureRemovalRequest {
		return new SignatureRemovalRequest(object);
	}

	/** @inheritDoc */
	protected transformSearchParams(
		searchParams: SignatureRemovalRequestSearchParamsInterface
	): SignatureRemovalRequestSearchParamsInterface {
		// Normalize created_at boundaries to UTC midnight / last second.
		if (typeof searchParams.created_at__min !== 'undefined') {
			searchParams.created_at__min = Helpers.toMidnightUTC(
				searchParams.created_at__min
			);
		}
		if (typeof searchParams.created_at__max !== 'undefined') {
			searchParams.created_at__max = Helpers.toLastSecondUTC(
				searchParams.created_at__max
			);
		}
		return searchParams;
	}

	/**
	 * Propose the removal of a signed presence attestation. Coach-only endpoint.
	 * Returns the created request document (status: 'pending').
	 */
	proposeRemoval(
		attestationId: string
	): Promise<SignatureRemovalRequestInterface> {
		const options = { withCredentials: true };
		return this.http
			.post<SignatureRemovalRequestInterface>(
				this.uri(),
				{ attestationId },
				options
			)
			.toPromise();
	}

	/** Admin: approve a pending request — deletes the attestation and flags status='approved'. */
	approve(id: string): Promise<SignatureRemovalRequestInterface> {
		const options = { withCredentials: true };
		return this.http
			.patch<SignatureRemovalRequestInterface>(
				`${this.uri(true)}/${id}/approve`,
				{},
				options
			)
			.toPromise();
	}

	/** Admin: reject a pending request — leaves the attestation untouched, flags status='rejected'. */
	reject(id: string): Promise<SignatureRemovalRequestInterface> {
		const options = { withCredentials: true };
		return this.http
			.patch<SignatureRemovalRequestInterface>(
				`${this.uri(true)}/${id}/reject`,
				{},
				options
			)
			.toPromise();
	}

	/**
	 * Return the set of attestation ids that have a pending removal request
	 * authored by the currently-logged-in coach for the given thematique.
	 * Used to render a "Demande en cours" badge in the formateur signature table.
	 */
	async listPendingAttestationIds(
		thematiqueId: string
	): Promise<Set<string>> {
		const options = {
			params: {
				thematique: thematiqueId,
				status: 'pending',
				_page: '0',
				_limit: '100',
			},
			withCredentials: true,
		};
		const res = await this.http
			.get<{ items: SignatureRemovalRequestInterface[] }>(
				this.uri(),
				options
			)
			.toPromise();
		const ids = new Set<string>();
		(res.items || []).forEach((item) => {
			if (item && item.attestation) ids.add(item.attestation);
		});
		return ids;
	}
}
